import { Action } from 'widgets-for-react'
import { Map, Set } from 'immutable'
import { validateField } from './newsletterFormBlockValidation'

export interface NewsletterFormBlockProps {
  readonly kind: "newsletterForm"
}

export interface NewsletterFormBlockState {
  data: FormState
  visited: Set<Field>
  validationMessages: Map<Field, string>
  status: 'initial' | 'submitting' | 'succeeded' | 'failed'
}

export const genderValues = ['V', 'M', 'A'] as const
type GenderValue = typeof genderValues[number]

export const genderLabels: { [n in GenderValue]: string } = {
  V: 'Vrouw',
  M: 'Man',
  A: 'Anders'
}

export const branches = [
  'Alblasserdam',
  'Arkel',
  'Barendrecht Carnisselande',
  'Barendrecht Het Kruispunt',
  'Bleskensgraaf',
  'Boven-Hardinxveld',
  'Dordrecht Crabbehof',
  'Dordrecht Dubbeldam',
  'Dordrecht Stadsbibliotheek',
  'Dordrecht Stadspolders',
  'Dordrecht Sterrenburg',
  'Giessenburg',
  'Gorinchem centrum',
  'Gorinchem Het Gebouw',
  'Groot-Ammers',
  'Hardinxveld-Giessendam',
  'Hendrik-Ido-Ambacht',
  'Heerjansdam',
  'Nieuw-Lekkerland',
  'Nieuwpoort',
  'Noordeloos',
  'Papendrecht',
  'Poortugaal',
  'Portland',
  'Ridderkerk Bolnes',
  'Ridderkerk centrum',
  'Ridderkerk Slikkerveer',
  'Sliedrecht',
  'Zwijndrecht Kubiek',
  'Zwijndrecht Walburg',
] as const

export type Branch = typeof branches[number]


export const newsletters = [
  'Nieuwsbrief',
  'Nieuwsbrief VE',
  'Nieuwsbrief PO',
  'Nieuwsbrief VO',
  'Nieuwsbrief Leesclubs',
  'Updates programmering en collectie',
  'Klantonderzoek',
] as const

export type Newsletter = typeof newsletters[number];

export const newsletterLabels: { [n in Newsletter]: string } = {
  'Nieuwsbrief': 'Blijf maandelijks op de hoogte van jouw bieb, om meer te beleven met de bibliotheek',
  'Nieuwsbrief VE': 'Uitnodigingen voor bijeenkomsten, tips en nieuws voor professionals in de voorschoolse educatie',
  'Nieuwsbrief PO': 'Uitnodigingen voor bijeenkomsten, tips en nieuws voor professionals in het primair onderwijs',
  'Nieuwsbrief VO': 'Uitnodigingen voor bijeenkomsten, tips en nieuws voor professionals in het voortgezet onderwijs',
  'Nieuwsbrief Leesclubs': 'De nieuwsbrief speciaal voor leesclubs. Met updates en leestips',
  'Updates programmering en collectie': 'Ontvang uitnodigingen en aanbiedingen die aansluiten bij jouw interesses',
  'Klantonderzoek': 'Geef je mening over de Bibliotheek AanZet',
}

export interface FormState {
  firstName: string
  initials: string
  infix: string
  lastName: string
  emailAddress: string
  dateOfBirth: Date
  gender: GenderValue
  branch: Branch,
  newsletters: Set<Newsletter>
}

export type Field = keyof FormState

export const initialFormState: FormState = {
  firstName: '',
  initials: '',
  infix: '',
  lastName: '',
  emailAddress: '',
  dateOfBirth: (() => {
    const date = new Date()
    date.setFullYear(date.getFullYear() - 25)
    return date
  })(),
  gender: 'A',
  branch: branches[0],
  newsletters: Set<Newsletter>()
}

export const allFields = Object.keys(initialFormState) as Array<Field>

export const initialState: NewsletterFormBlockState = {
  data: initialFormState,
  visited: Set<Field>(),
  validationMessages: Map<Field, string>(),
  status: 'initial'
}

export const setFieldOnState =
  (setState: (action: Action<NewsletterFormBlockState>) => void) =>
    <Field extends keyof FormState>(field: Field) =>
      (value: FormState[Field]) =>
        setState((s) => ({
          ...s, data: { ...s.data, [field]: value }
        }))

export const toggleNewsletterOnState =
  (setState: (action: Action<NewsletterFormBlockState>) => void) =>
    (value: Newsletter) =>
      setState((s) => ({
        ...s, data: {
          ...s.data,
          newsletters: s.data.newsletters.has(value)
            ? s.data.newsletters.remove(value)
            : s.data.newsletters.add(value)
        }
      }))

export const setVisited =
  (setState: (action: Action<NewsletterFormBlockState>) => void) => (field: Field) =>
    setState((s) => ({ ...s, visited: s.visited.add(field) }))

export const validateFormData =
  (setState: (action: Action<NewsletterFormBlockState>) => void) =>
    (all?: boolean) => setState((prevState) => {
      const fields = all ? allFields : prevState.visited.toArray()

      const validationMessages = validateFields(fields, prevState.data)

      // When "all fields" is triggered once, ensure all is validated on updates
      const visited = Set(fields)

      return { ...prevState, validationMessages, visited }
    })

export const validateFields = (fields: Field[], data: FormState) => fields.reduce<Map<Field, string>>((messages, field) => {
  const result = validateField(field)(data[field])
  return result.status == 'invalid'
    ? messages.set(field, result.message)
    : messages
}, Map<Field, string>())




